// src/components/about/PrivacyPolicyDialog.tsx

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    IconButton,
} from '@mui/material';
import {
    Close as CloseIcon,
    Policy as PolicyIcon,
} from '@mui/icons-material';

interface PrivacyPolicyDialogProps {
    open: boolean;
    onClose: () => void;
}

const PrivacyPolicyDialog: React.FC<PrivacyPolicyDialogProps> = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: { borderRadius: 2 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PolicyIcon color="primary" />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        Политика конфиденциальности
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Редакция от 1 сентября 2025 года
                </Typography>

                {/* 1. Общие положения */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    1. Общие положения
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                    Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок обработки
                    и защиты персональных данных пользователей платформы rusyaikina-tutor (далее — «Платформа»),
                    расположенной по адресу https://rusyaikina-tutor.ru.

                    Оператором персональных данных является владелец Платформы (далее — «Оператор»).

                    Используя Платформу, Пользователь подтверждает, что ознакомился с настоящей Политикой
                    и дает согласие на обработку своих персональных данных в соответствии с описанными условиями.
                </Typography>

                {/* 2. Какие данные мы собираем */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    2. Какие данные мы собираем
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Мы собираем только те данные, которые необходимы для организации учебного процесса:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                    <li><Typography variant="body2">Фамилия, имя, отчество</Typography></li>
                    <li><Typography variant="body2">Номер телефона</Typography></li>
                    <li><Typography variant="body2">Адрес электронной почты</Typography></li>
                    <li><Typography variant="body2">Ссылки на профили в мессенджерах (Telegram, VK) — при добровольном указании</Typography></li>
                    <li><Typography variant="body2">Данные об уроках: расписание, темы занятий, стоимость</Typography></li>
                </Box>

                {/* 3. Цели обработки */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    3. Цели обработки персональных данных
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Персональные данные обрабатываются исключительно для:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                    <li><Typography variant="body2">Регистрации и авторизации пользователей</Typography></li>
                    <li><Typography variant="body2">Организации и проведения учебных занятий</Typography></li>
                    <li><Typography variant="body2">Ведения расписания и учета уроков</Typography></li>
                    <li><Typography variant="body2">Связи между репетитором и учениками</Typography></li>
                    <li><Typography variant="body2">Улучшения качества работы Платформы</Typography></li>
                </Box>

                {/* 4. Правовые основания */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    4. Правовые основания обработки
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Обработка персональных данных осуществляется на основании:
                    согласия субъекта персональных данных (ст. 9 Федерального закона от 27.07.2006 № 152-ФЗ
                    «О персональных данных»), а также в целях исполнения договора об оказании услуг.
                </Typography>

                {/* 5. Передача третьим лицам */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    5. Передача данных третьим лицам
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Персональные данные Пользователей <strong>не передаются третьим лицам</strong>,
                    за исключением случаев, предусмотренных законодательством Российской Федерации.
                    Данные не используются для рекламных рассылок и не продаются.
                </Typography>

                {/* 6. Хранение данных */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    6. Хранение и защита данных
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Персональные данные граждан Российской Федерации хранятся на серверах,
                    расположенных <strong>на территории Российской Федерации</strong>,
                    в соответствии с требованиями ч. 5 ст. 18 Федерального закона № 152-ФЗ.

                    Оператор принимает необходимые организационные и технические меры для защиты
                    персональных данных от неправомерного доступа, уничтожения, изменения, блокирования.
                </Typography>

                {/* 7. Права пользователя */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    7. Права пользователя
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Пользователь имеет право:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                    <li><Typography variant="body2">Запросить информацию об обработке своих персональных данных</Typography></li>
                    <li><Typography variant="body2">Требовать уточнения, блокирования или уничтожения данных</Typography></li>
                    <li><Typography variant="body2">Отозвать согласие на обработку персональных данных</Typography></li>
                    <li><Typography variant="body2">Обжаловать действия Оператора в Роскомнадзоре или в суде</Typography></li>
                </Box>

                {/* 8. Контакты */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    8. Контакты
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    По всем вопросам, связанным с обработкой персональных данных,
                    вы можете обратиться к Оператору через vk: <strong>https://vk.ru/divnaya_nina</strong>.
                </Typography>

                {/* 9. Изменения */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                    9. Изменения в Политике
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Оператор вправе вносить изменения в настоящую Политику. Новая редакция вступает в силу
                    с момента её публикации на Платформе. Продолжая использовать Платформу после публикации
                    изменений, Пользователь принимает новую редакцию Политики.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                <Button onClick={onClose} variant="contained">
                    Понятно
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrivacyPolicyDialog;